import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User,
  Building2,
  CreditCard,
  Bell,
  Link2,
  Shield,
  Check,
  ChevronRight,
  ExternalLink,
  Database,
  Fuel,
  Wrench,
  FileSpreadsheet,
  ScanLine,
  Save,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { useAuthStore, useAppStore, usePricingStore } from '../store';
import { SUBSCRIPTION_PLANS, formatCurrency, formatFuelPrice } from '@jrd/core';

const INF0TRON_ASCII = `
██╗███╗   ██╗███████╗ ██████╗ ████████╗██████╗  ██████╗ ███╗   ██╗
██║████╗  ██║██╔════╝██╔═══██╗╚══██╔══╝██╔══██╗██╔═══██╗████╗  ██║
██║██╔██╗ ██║█████╗  ██║   ██║   ██║   ██████╔╝██║   ██║██╔██╗ ██║
██║██║╚██╗██║██╔══╝  ██║   ██║   ██║   ██╔══██╗██║   ██║██║╚██╗██║
██║██║ ╚████║██║     ╚██████╔╝   ██║   ██║  ██║╚██████╔╝██║ ╚████║
╚═╝╚═╝  ╚═══╝╚═╝      ╚═════╝    ╚═╝   ╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═══╝
`;

// Inf0tron Data Editor Component
function Inf0tronTab() {
  const { locations } = useAppStore();
  const {
    stationPrices,
    setStationPrices,
    wholesalePrices,
    setWholesalePrice,
  } = usePricingStore();

  const [selectedApp, setSelectedApp] = useState<'price-o-tron' | 'aut0tron' | 'docutron' | 'scan-o-tron'>('price-o-tron');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Local state for editing
  const [priceOverrides, setPriceOverrides] = useState<Record<string, Record<string, number>>>({});
  const [wholesaleOverrides, setWholesaleOverrides] = useState<Record<string, number>>({});
  const [laborRateOverride, setLaborRateOverride] = useState<number>(125);
  const [partsMarkupOverride, setPartsMarkupOverride] = useState<number>(60);

  const gasLocations = locations.filter((l) => l.industry === 'gas' || l.industry === 'both');
  const autoLocations = locations.filter((l) => l.industry === 'auto' || l.industry === 'both');

  const handleSave = () => {
    setSaveStatus('saving');

    // Apply price overrides
    Object.entries(priceOverrides).forEach(([locationId, grades]) => {
      const location = gasLocations.find((l) => l.id === locationId);
      if (location?.gasbuddyId) {
        const existingPrices = stationPrices[location.gasbuddyId];
        if (existingPrices) {
          const updatedPrices = existingPrices.prices.map((p) => ({
            ...p,
            price: grades[p.grade] !== undefined ? grades[p.grade] : p.price,
          }));
          setStationPrices(location.gasbuddyId, {
            ...existingPrices,
            prices: updatedPrices,
          });
        }
      }
    });

    // Apply wholesale overrides
    Object.entries(wholesaleOverrides).forEach(([locationId, cost]) => {
      const existing = wholesalePrices[locationId];
      setWholesalePrice(locationId, {
        locationId,
        supplier: existing?.supplier || 'Manual Override',
        grade: '87',
        cost,
        effectiveDate: new Date(),
        source: 'manual',
      });
    });

    setTimeout(() => {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }, 500);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="card">
        <div className="card-body">
          <pre className="text-jrd-orange text-[5px] sm:text-[6px] font-mono leading-none mb-3">
            {INF0TRON_ASCII}
          </pre>
          <p className="text-text-secondary text-sm">
            Manual data overrides when connectors fail. Edit values here to keep your system running.
          </p>
          <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
            <span className="text-yellow-500 text-xs">
              Manual overrides will be replaced when connectors sync successfully
            </span>
          </div>
        </div>
      </div>

      {/* App Selector */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { id: 'price-o-tron', label: 'Price-O-Tron', icon: Fuel, color: 'jrd-orange' },
          { id: 'aut0tron', label: 'Aut0tron', icon: Wrench, color: 'jrd-green' },
          { id: 'docutron', label: 'Docutron', icon: FileSpreadsheet, color: 'jrd-orange' },
          { id: 'scan-o-tron', label: 'Scan-O-Tron', icon: ScanLine, color: 'jrd-green' },
        ].map(({ id, label, icon: Icon, color }) => (
          <button
            key={id}
            onClick={() => setSelectedApp(id as typeof selectedApp)}
            className={`flex items-center gap-2 px-4 py-2 rounded transition-all ${
              selectedApp === id
                ? `bg-${color}/20 text-${color} border border-${color}/50`
                : 'bg-bg-card border border-text-muted/20 text-text-secondary hover:text-text-primary'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Price-O-Tron Data */}
      {selectedApp === 'price-o-tron' && (
        <div className="space-y-4">
          {gasLocations.length === 0 ? (
            <div className="card card-body text-center py-8 text-text-muted">
              <Fuel className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <div>No gas stations configured</div>
            </div>
          ) : (
            gasLocations.map((location) => {
              const prices = location.gasbuddyId ? stationPrices[location.gasbuddyId] : null;
              const wholesale = wholesalePrices[location.id];

              return (
                <div key={location.id} className="card">
                  <div className="card-header flex items-center justify-between">
                    <span>// {location.name.toUpperCase()}</span>
                    <span className="text-text-muted text-xs">
                      {location.gasbuddyId ? `GasBuddy: ${location.gasbuddyId}` : 'No GasBuddy ID'}
                    </span>
                  </div>
                  <div className="card-body space-y-4">
                    {/* Retail Prices */}
                    <div>
                      <div className="text-text-muted text-xs font-mono mb-2">RETAIL PRICES</div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {(['87', '89', '91', 'diesel'] as const).map((grade) => {
                          const currentPrice = prices?.prices.find((p) => p.grade === grade)?.price || 0;
                          const override = priceOverrides[location.id]?.[grade];

                          return (
                            <div key={grade} className="space-y-1">
                              <label className="text-text-secondary text-xs">
                                {grade === '87' ? '87 Unleaded' : grade.toUpperCase()}
                              </label>
                              <div className="flex items-center gap-2">
                                <span className="text-text-muted">$</span>
                                <input
                                  type="number"
                                  step="0.001"
                                  value={override !== undefined ? override : currentPrice || ''}
                                  onChange={(e) => {
                                    setPriceOverrides((prev) => ({
                                      ...prev,
                                      [location.id]: {
                                        ...prev[location.id],
                                        [grade]: parseFloat(e.target.value) || 0,
                                      },
                                    }));
                                  }}
                                  placeholder={currentPrice ? currentPrice.toFixed(3) : '0.000'}
                                  className="w-full bg-bg-input border border-text-muted/30 rounded px-2 py-1 text-sm font-mono"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Wholesale Cost */}
                    <div>
                      <div className="text-text-muted text-xs font-mono mb-2">WHOLESALE COST (87)</div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 flex-1 max-w-xs">
                          <span className="text-text-muted">$</span>
                          <input
                            type="number"
                            step="0.001"
                            value={wholesaleOverrides[location.id] !== undefined ? wholesaleOverrides[location.id] : wholesale?.cost || ''}
                            onChange={(e) => {
                              setWholesaleOverrides((prev) => ({
                                ...prev,
                                [location.id]: parseFloat(e.target.value) || 0,
                              }));
                            }}
                            placeholder={wholesale?.cost ? wholesale.cost.toFixed(3) : '0.000'}
                            className="w-full bg-bg-input border border-text-muted/30 rounded px-2 py-1 text-sm font-mono"
                          />
                          <span className="text-text-muted text-sm">/gal</span>
                        </div>
                        <span className="text-text-muted text-xs">
                          Source: {wholesale?.supplier || 'Not set'}
                        </span>
                      </div>
                    </div>

                    {/* Calculated Margin */}
                    <div className="p-3 bg-bg-input rounded border border-text-muted/20">
                      <div className="flex items-center justify-between">
                        <span className="text-text-secondary text-sm">Calculated Margin (87)</span>
                        <span className="text-jrd-green font-mono font-bold">
                          {(() => {
                            const retail = priceOverrides[location.id]?.['87'] ?? prices?.prices.find((p) => p.grade === '87')?.price ?? 0;
                            const cost = wholesaleOverrides[location.id] ?? wholesale?.cost ?? 0;
                            const margin = retail > 0 && cost > 0 ? retail - cost : 0;
                            return margin > 0 ? `${Math.round(margin * 100)}¢/gal` : 'N/A';
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Aut0tron Data */}
      {selectedApp === 'aut0tron' && (
        <div className="space-y-4">
          {autoLocations.length === 0 ? (
            <div className="card card-body text-center py-8 text-text-muted">
              <Wrench className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <div>No auto shops configured</div>
            </div>
          ) : (
            autoLocations.map((location) => (
              <div key={location.id} className="card">
                <div className="card-header">// {location.name.toUpperCase()}</div>
                <div className="card-body space-y-4">
                  {/* Labor Rate */}
                  <div>
                    <div className="text-text-muted text-xs font-mono mb-2">LABOR RATE</div>
                    <div className="flex items-center gap-2 max-w-xs">
                      <span className="text-text-muted">$</span>
                      <input
                        type="number"
                        step="1"
                        value={laborRateOverride}
                        onChange={(e) => setLaborRateOverride(parseFloat(e.target.value) || 0)}
                        className="w-full bg-bg-input border border-text-muted/30 rounded px-2 py-1 text-sm font-mono"
                      />
                      <span className="text-text-muted text-sm">/hr</span>
                    </div>
                  </div>

                  {/* Parts Markup */}
                  <div>
                    <div className="text-text-muted text-xs font-mono mb-2">PARTS MARKUP</div>
                    <div className="flex items-center gap-2 max-w-xs">
                      <input
                        type="number"
                        step="1"
                        value={partsMarkupOverride}
                        onChange={(e) => setPartsMarkupOverride(parseFloat(e.target.value) || 0)}
                        className="w-full bg-bg-input border border-text-muted/30 rounded px-2 py-1 text-sm font-mono"
                      />
                      <span className="text-text-muted">%</span>
                    </div>
                  </div>

                  {/* Mitchell1 Connection */}
                  <div>
                    <div className="text-text-muted text-xs font-mono mb-2">MITCHELL1 CONNECTION</div>
                    <div className="p-3 bg-bg-input rounded border border-text-muted/20">
                      <div className="flex items-center justify-between">
                        <span className="text-text-secondary">Shop Code</span>
                        <input
                          type="text"
                          placeholder="Enter shop code"
                          className="bg-transparent border-b border-text-muted/30 px-2 py-1 text-sm font-mono text-right focus:outline-none focus:border-jrd-green"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Docutron Data */}
      {selectedApp === 'docutron' && (
        <div className="card">
          <div className="card-header">// DOCUTRON CONFIG</div>
          <div className="card-body space-y-4">
            <div>
              <div className="text-text-muted text-xs font-mono mb-2">QBO REALM ID</div>
              <input
                type="text"
                placeholder="Enter QBO Realm ID"
                className="w-full bg-bg-input border border-text-muted/30 rounded px-3 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <div className="text-text-muted text-xs font-mono mb-2">DEFAULT ACCOUNTS</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-text-secondary text-xs">EFT Clearing Account</label>
                  <input
                    type="text"
                    placeholder="Account ID"
                    className="w-full bg-bg-input border border-text-muted/30 rounded px-2 py-1 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="text-text-secondary text-xs">AP Clearing Account</label>
                  <input
                    type="text"
                    placeholder="Account ID"
                    className="w-full bg-bg-input border border-text-muted/30 rounded px-2 py-1 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="text-text-secondary text-xs">Sales Account</label>
                  <input
                    type="text"
                    placeholder="Account ID"
                    className="w-full bg-bg-input border border-text-muted/30 rounded px-2 py-1 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="text-text-secondary text-xs">Bank Account</label>
                  <input
                    type="text"
                    placeholder="Account ID"
                    className="w-full bg-bg-input border border-text-muted/30 rounded px-2 py-1 text-sm font-mono"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scan-O-Tron Data */}
      {selectedApp === 'scan-o-tron' && (
        <div className="card">
          <div className="card-header">// SCAN-O-TRON CONFIG</div>
          <div className="card-body space-y-4">
            <div>
              <div className="text-text-muted text-xs font-mono mb-2">OCR SETTINGS</div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-bg-input rounded border border-text-muted/20">
                  <span className="text-text-secondary">Auto-detect document type</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-text-muted/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-jrd-green"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between p-3 bg-bg-input rounded border border-text-muted/20">
                  <span className="text-text-secondary">Extract line items</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-text-muted/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-jrd-green"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saveStatus === 'saving'}
          className="btn-primary flex items-center gap-2"
        >
          {saveStatus === 'saving' ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : saveStatus === 'saved' ? (
            <Check className="w-4 h-4" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saveStatus === 'saved' ? 'Saved!' : 'Save Overrides'}
        </button>
      </div>
    </motion.div>
  );
}

export function Settings() {
  const { user } = useAuthStore();
  const { locations } = useAppStore();
  const [activeTab, setActiveTab] = useState<'account' | 'billing' | 'locations' | 'connections' | 'notifications' | 'inf0tron'>('account');

  const currentPlan = SUBSCRIPTION_PLANS[user?.subscriptionTier || 'trial'];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Settings</h1>
        <p className="text-text-muted text-sm font-mono">
          <span className="text-jrd-orange">//</span> Account & configuration
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="p-2 space-y-1">
              {[
                { id: 'account', label: 'Account', icon: User },
                { id: 'billing', label: 'Billing', icon: CreditCard },
                { id: 'locations', label: 'Locations', icon: Building2 },
                { id: 'connections', label: 'Connections', icon: Link2 },
                { id: 'notifications', label: 'Notifications', icon: Bell },
                { id: 'inf0tron', label: 'Inf0tron', icon: Database },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id as typeof activeTab)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded transition-all ${
                    activeTab === id
                      ? id === 'inf0tron'
                        ? 'bg-jrd-green/10 text-jrd-green'
                        : 'bg-jrd-orange/10 text-jrd-orange'
                      : 'text-text-secondary hover:text-text-primary hover:bg-bg-card-hover'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Inf0tron Tab */}
          {activeTab === 'inf0tron' && <Inf0tronTab />}

          {/* Account Tab */}
          {activeTab === 'account' && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div className="card">
                <div className="card-header">// PROFILE</div>
                <div className="card-body space-y-4">
                  <div>
                    <label className="block text-text-muted text-xs font-mono mb-1 uppercase tracking-wide">
                      Email
                    </label>
                    <input
                      type="email"
                      defaultValue={user?.email}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-text-muted text-xs font-mono mb-1 uppercase tracking-wide">
                      Company Name
                    </label>
                    <input
                      type="text"
                      defaultValue={user?.companyName}
                      className="w-full"
                    />
                  </div>
                  <button className="btn-primary">Save Changes</button>
                </div>
              </div>

              <div className="card">
                <div className="card-header">// SECURITY</div>
                <div className="card-body space-y-4">
                  <button className="btn-ghost w-full justify-between">
                    <span className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Change Password
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button className="btn-ghost w-full justify-between">
                    <span className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Two-Factor Authentication
                    </span>
                    <span className="text-text-muted text-xs">Not enabled</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              {/* Current Plan */}
              <div className="card border-jrd-orange/30">
                <div className="card-header flex items-center justify-between">
                  <span>// CURRENT PLAN</span>
                  {user?.subscriptionTier === 'trial' && (
                    <span className="text-jrd-yellow text-xs">
                      Trial ends in {Math.ceil((new Date(user.trialEndsAt!).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} days
                    </span>
                  )}
                </div>
                <div className="card-body">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-jrd-orange">{currentPlan.name}</div>
                      <div className="text-text-secondary">
                        {currentPlan.price > 0 ? `$${currentPlan.price}/month` : 'Free'}
                      </div>
                    </div>
                    <button className="btn-primary">
                      {user?.subscriptionTier === 'trial' ? 'Upgrade' : 'Manage'}
                    </button>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {currentPlan.features.map((feature) => (
                      <span
                        key={feature}
                        className="inline-flex items-center gap-1 text-xs bg-jrd-green/10 text-jrd-green px-2 py-1 rounded"
                      >
                        <Check className="w-3 h-3" />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Available Plans */}
              <div className="card">
                <div className="card-header">// AVAILABLE PLANS</div>
                <div className="card-body">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.values(SUBSCRIPTION_PLANS)
                      .filter((p) => p.tier !== 'trial')
                      .map((plan) => (
                        <div
                          key={plan.tier}
                          className={`p-4 rounded-lg border ${
                            plan.tier === user?.subscriptionTier
                              ? 'border-jrd-orange bg-jrd-orange/5'
                              : 'border-text-muted/20'
                          }`}
                        >
                          <div className="text-lg font-bold text-text-primary">{plan.name}</div>
                          <div className="text-2xl font-bold text-jrd-green mt-1">
                            ${plan.price}<span className="text-sm text-text-muted">/mo</span>
                          </div>
                          <ul className="mt-3 space-y-1 text-sm text-text-secondary">
                            {plan.features.map((f) => (
                              <li key={f} className="flex items-center gap-2">
                                <Check className="w-3 h-3 text-jrd-green" />
                                {f}
                              </li>
                            ))}
                          </ul>
                          <button
                            className={`w-full mt-4 ${
                              plan.tier === user?.subscriptionTier ? 'btn-ghost' : 'btn-primary'
                            }`}
                            disabled={plan.tier === user?.subscriptionTier}
                          >
                            {plan.tier === user?.subscriptionTier ? 'Current' : 'Select'}
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Locations Tab */}
          {activeTab === 'locations' && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="card"
            >
              <div className="card-header flex items-center justify-between">
                <span>// LOCATIONS</span>
                <button className="btn-primary px-3 py-1 text-sm">Add Location</button>
              </div>
              <div className="card-body">
                {locations.length === 0 ? (
                  <div className="text-center py-8 text-text-muted">
                    <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <div>No locations configured</div>
                    <button className="btn-primary mt-4">Add Your First Location</button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {locations.map((loc) => (
                      <div
                        key={loc.id}
                        className="p-4 bg-bg-input rounded border border-text-muted/20"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-text-primary font-medium">{loc.name}</div>
                            <div className="text-text-muted text-sm">{loc.address}</div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                loc.industry === 'gas' ? 'bg-jrd-orange/20 text-jrd-orange' :
                                loc.industry === 'auto' ? 'bg-jrd-green/20 text-jrd-green' :
                                'bg-text-muted/20 text-text-muted'
                              }`}>
                                {loc.industry.toUpperCase()}
                              </span>
                              {loc.gasbuddyId && (
                                <span className="text-xs text-text-muted">
                                  GasBuddy: {loc.gasbuddyId}
                                </span>
                              )}
                            </div>
                          </div>
                          <button className="btn-ghost px-3 py-1 text-sm">Edit</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Connections Tab */}
          {activeTab === 'connections' && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="card"
            >
              <div className="card-header">// API CONNECTIONS</div>
              <div className="card-body space-y-4">
                {[
                  { name: 'QuickBooks Online', status: 'connected', realm: 'DY Holdings (Read Only)' },
                  { name: 'Microsoft 365', status: 'not_connected', realm: null },
                  { name: 'Mitchell1', status: 'connected', realm: 'Maplewood Auto' },
                  { name: 'GasBuddy', status: 'connected', realm: 'API Key Active' },
                  { name: 'Cloudflare', status: 'connected', realm: 'D1 + Workers' },
                ].map((conn) => (
                  <div
                    key={conn.name}
                    className="flex items-center justify-between p-3 bg-bg-input rounded border border-text-muted/20"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        conn.status === 'connected' ? 'bg-jrd-green' : 'bg-text-muted'
                      }`} />
                      <div>
                        <div className="text-text-primary">{conn.name}</div>
                        {conn.realm && (
                          <div className="text-text-muted text-xs">{conn.realm}</div>
                        )}
                      </div>
                    </div>
                    <button className={`${
                      conn.status === 'connected' ? 'btn-ghost' : 'btn-primary'
                    } px-3 py-1 text-sm`}>
                      {conn.status === 'connected' ? 'Configure' : 'Connect'}
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="card"
            >
              <div className="card-header">// NOTIFICATION PREFERENCES</div>
              <div className="card-body space-y-4">
                {[
                  { name: 'Price Alerts', desc: 'When competitor prices change significantly' },
                  { name: 'Low Tank Alerts', desc: 'When fuel levels drop below reorder point' },
                  { name: 'File Processing', desc: 'When Docutron completes file processing' },
                  { name: 'Daily Summary', desc: 'Daily margin and operations summary' },
                ].map((notif) => (
                  <div
                    key={notif.name}
                    className="flex items-center justify-between p-3 bg-bg-input rounded border border-text-muted/20"
                  >
                    <div>
                      <div className="text-text-primary">{notif.name}</div>
                      <div className="text-text-muted text-xs">{notif.desc}</div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-text-muted/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-jrd-green"></div>
                    </label>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
